import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { cn } from "@/lib/utils";
import {
  getUnexpiredToken,
  logAxiosError,
  useActiveMainCanvas,
  useActiveProject,
  useCanvasId,
  usePrevious,
} from "@/utils/utils";
import { useSelector, useDispatch } from "react-redux";

import { useLaunchContainer, useStartServer } from "@/utils/devServerUtils";
import { fetchDevServerStatus } from "@/store/codebaseSlice";
import { Loader2, PlusIcon } from "lucide-react";
import { Button } from "./ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ProjectRunMode } from "@/utils/templateProjects";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLocalMode from "@/hooks/useLocalMode";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { detectViteErrors } from "@/utils/console";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { STORYBOARD_HYDRATION_STATUS } from "@/utils/constants";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import QRCode from "react-qr-code";
import { useTypedSelector } from "@/store/store";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import { useSession } from "@clerk/clerk-react";
import { isKubernetesMode } from "@/utils/deploymentMode";
import { useKubernetesTerminal } from "./KubernetesTerminal";

interface Props {
  readOnly?: boolean;
  connect: boolean;
}

// Remove enum Tabs and replace with string constants for tab IDs
const Tabs = {
  SERVER: "server",
  TERMINAL: "terminal",
} as const;
type Tabs = (typeof Tabs)[keyof typeof Tabs];

interface ConsoleLoaderProps {
  count: number;
  onReconnect: () => void;
  reconnecting: boolean;
}

const ConsoleSkeleton = () => {
  const getRandomWidth = (): string => {
    const widths = [
      "1/12",
      "2/12",
      "3/12",
      "4/12",
      "5/12",
      "7/12",
      "8/12",
      "9/12",
      "10/12",
    ];
    return widths[Math.floor(Math.random() * widths.length)];
  };

  return (
    <Skeleton
      className={`h-2 w-${getRandomWidth()} rounded-xs bg-secondary-300 shrink-0`}
    />
  );
};

const ConsoleLoader: React.FC<ConsoleLoaderProps> = ({
  count,
  onReconnect,
  reconnecting,
}) => {
  const skeletonElements = useMemo(() => {
    return Array.from({ length: count }, (_, index) => (
      <ConsoleSkeleton key={index} />
    ));
  }, [count]);

  return (
    <div className="p-2 w-full flex flex-col gap-1.5 relative h-full overflow-hidden">
      {skeletonElements}
      <Button
        className={cn(
          // Put in middle absolutely
          "absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          !onReconnect && "hidden"
        )}
        variant="outline"
        size="sm"
        onClick={() => {
          onReconnect();
        }}
      >
        <div className={cn("animate-spin mr-1", !reconnecting && "hidden")}>
          <Loader2 className="h-4 w-4" />
        </div>
        Reconnect
      </Button>
    </div>
  );
};

const Console: React.FC<Props> = ({ readOnly, connect }) => {
  // Simple approach: each terminal gets its own component instance
  const [selectedTab, setSelectedTab] = React.useState<string>(Tabs.SERVER);
  const [terminals, setTerminals] = useState<
    Array<{ id: string; name: string }>
  >([
    { id: Tabs.SERVER, name: "Console" }, // Start with Console terminal
  ]);

  // Store separate terminal instances for each tab
  const [terminalInstances, setTerminalInstances] = useState<{
    [terminalId: string]: any;
  }>({});

  // Add per-terminal state management
  const [terminalStates, setTerminalStates] = useState<{
    [terminalId: string]: {
      initialized: boolean;
      initializing: boolean;
    };
  }>({});

  // Store separate container refs for each terminal
  const [terminalContainers, setTerminalContainers] = useState<{
    [terminalId: string]: HTMLDivElement;
  }>({});

  // Store the dev server WebSocket for control commands
  const devServerSocketRef = useRef<WebSocket | null>(null);

  // Store console output for potential copying to new terminals
  const [consoleOutput, setConsoleOutput] = useState<string>("");
  const consoleOutputRef = useRef<string>("");

  // Helper functions for terminal state management
  const getTerminalState = (terminalId: string) => {
    return (
      terminalStates[terminalId] || { initialized: false, initializing: false }
    );
  };

  const setTerminalState = (
    terminalId: string,
    state: { initialized?: boolean; initializing?: boolean }
  ) => {
    setTerminalStates((prev) => ({
      ...prev,
      [terminalId]: {
        ...getTerminalState(terminalId),
        ...state,
      },
    }));
  };

  const projectLaunchStatus = useCodebaseStore(
    (state) => state.projectLaunchStatus
  );
  const projectLaunchStatusRef = useRef(projectLaunchStatus);
  useEffect(() => {
    projectLaunchStatusRef.current = projectLaunchStatus;
  }, [projectLaunchStatus]);

  const { isLocalMode, localPort } = useLocalMode();

  const terminalContainerRef = useRef<HTMLDivElement>();
  const [terminalContainerMounted, setTerminalContainerMounted] =
    useState(false);
  const serverContainerRef = useRef<HTMLDivElement>();
  const [serverContainerMounted, setServerContainerMounted] = useState(false);
  const parentContainer = useRef<HTMLDivElement>();
  const [parentContainerMounted, setParentContainerMounted] = useState(false);

  const addTerminalErrors = useDevServerStore(
    (state) => state.addTerminalErrors
  );
  const setConvexLoginUrl = useCanvasStore((state) => state.setConvexLoginUrl);
  const convexLoginUrl = useCanvasStore((state) => state.convexLoginUrl);

  const setExpoQrCodeUrl = useCanvasStore((state) => state.setExpoQrCodeUrl);

  const conditions = useCodebaseStore((state) => state.conditions);
  const userLoggedIn = Boolean(
    conditions["convexInstalled"]?.data?.userLoggedIn
  );

  const canvasId = useCanvasId();

  const { session } = useSession();

  const project = useActiveProject();
  const mainCanvas = useActiveMainCanvas();

  const containerId = mainCanvas?.container_id;

  const projectLoadingProgress: any = useTypedSelector(
    (state) => state.supabaseStore.projectLoadingProgress
  );

  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const [reconnecting, setReconnecting] = useState(false);
  const [lastConnectingtime, setLastConnectingTime] = useState(null);

  const storyboardIdToError = useDevServerStore(
    (store) => store.storyboardIdToError
  );
  const storyboardIdToInternalError = useTypedSelector(
    (state) => state.iframe.storyboardIdToInternalError
  );

  const anyErrorsAtAll = useMemo(() => {
    const externalErrors =
      Object.values(storyboardIdToError).filter((error) => error).length > 0;
    const internalErrors =
      Object.values(storyboardIdToInternalError).filter((error: any) => {
        return (
          error?.status === STORYBOARD_HYDRATION_STATUS.ERROR ||
          error?.status === STORYBOARD_HYDRATION_STATUS.OTHER_ERROR
        );
      }).length > 0;

    return externalErrors || internalErrors;
  }, [storyboardIdToError, storyboardIdToInternalError]);
  const anyErrorsAtAllRef = useRef(anyErrorsAtAll);
  useEffect(() => {
    anyErrorsAtAllRef.current = anyErrorsAtAll;
  }, [anyErrorsAtAll]);

  const previousDeverServerTerminalRef = useRef<any>(null);
  const previousTerminalRef = useRef<any>(null);

  const terminalAddonRef = useRef<any>(null);
  const serverAddonRef = useRef<any>(null);

  // Kubernetes-specific terminal management (only used when isKubernetesMode() is true)
  const kubernetesTerminal = useKubernetesTerminal({
    terminalContainerRef,
    parentContainer,
    previousTerminalRef,
    terminalAddonRef,
    project,
    terminalId: selectedTab === Tabs.SERVER ? undefined : selectedTab, // Only pass terminal ID for actual terminals, not console
  });

  enum SocketStatus {
    Idle,
    Connecting,
    Open,
    Error,
    Closed,
  }

  const highestDevServerTryIndex = useRef(0);
  const [devServerSocketStatus, setDevServerSocketStatus] = useState(
    SocketStatus.Idle
  );

  const devServerStatus = useSelector(
    (state: any) => state.codebase.devServerStatus
  );

  const startServer = useStartServer();
  const launchContainer = useLaunchContainer();

  const startServerRef = useRef(startServer);
  useEffect(() => {
    startServerRef.current = startServer;
  }, [startServer]);

  const launchContainerRef = useRef(launchContainer);
  useEffect(() => {
    launchContainerRef.current = launchContainer;
  }, [launchContainerRef]);

  const restartedServerFromErrorRef = useRef(false);

  const [showConvexLoginDialog, setShowConvexLoginDialog] = useState(false);
  const [showExpoQRCodeDialog, setShowExpoQRCodeDialog] = useState(false);

  useEffect(() => {
    // Show only the selected terminal container, hide others
    Object.keys(terminalContainers).forEach((terminalId) => {
      const container = terminalContainers[terminalId];
      if (container) {
        if (terminalId === selectedTab) {
          container.style.display = "block";
        } else {
          container.style.display = "none";
        }
      }
    });

    setTimeout(() => {
      if (serverAddonRef.current) {
        serverAddonRef.current.fit();
      }

      if (terminalAddonRef.current) {
        terminalAddonRef.current.fit();
      }

      // Fit the active terminal
      const activeTerminal = terminalInstances[selectedTab];
      if (activeTerminal) {
        // Find the fit addon for this terminal
        const addons = (activeTerminal as any)._addonManager?._addons;
        if (addons) {
          const fitAddon = Object.values(addons).find(
            (addon: any) => addon.constructor.name === "FitAddon"
          );
          if (fitAddon) {
            (fitAddon as any).fit();
          }
        }
      }
    }, 0);
  }, [selectedTab, terminalContainers, terminalInstances]);

  const initTerminal = async () => {
    if (isKubernetesMode()) {
      // Get or create terminal instance for current tab
      const currentTerminalId = selectedTab;
      let terminalInstance = terminalInstances[currentTerminalId];

      if (!terminalInstance) {
        // Create new terminal instance for this tab
        const { Terminal } = await import("@xterm/xterm");
        const { FitAddon } = await import("@xterm/addon-fit");

        // Create a new container for this terminal if it doesn't exist
        let terminalContainer = terminalContainers[currentTerminalId];
        if (!terminalContainer) {
          // Create a new div element for this terminal
          terminalContainer = document.createElement("div");
          terminalContainer.style.width = "100%";
          terminalContainer.style.height = "100%";

          // Add it to the main terminal container
          if (terminalContainerRef.current) {
            terminalContainerRef.current.appendChild(terminalContainer);
          }

          // Store the container reference
          setTerminalContainers((prev) => ({
            ...prev,
            [currentTerminalId]: terminalContainer,
          }));
        }

        terminalInstance = new Terminal({
          theme: {
            background: "#1a1b1d",
            foreground: "white",
            cursor: "#6448ef",
          },
          fontSize: 12,
          fontWeight: 500,
          cursorInactiveStyle: "outline",
          cols: 100, // Set explicit column count to prevent 80-character limit
        });

        const fitAddon = new FitAddon();
        terminalInstance.loadAddon(fitAddon);
        terminalInstance.open(terminalContainer);
        fitAddon.fit();

        // Store the terminal instance
        setTerminalInstances((prev) => {
          const updated = { ...prev, [currentTerminalId]: terminalInstance };
          if (
            currentTerminalId === "console-terminal" &&
            consoleOutputRef.current
          ) {
            terminalInstance.write(consoleOutputRef.current);
            console.log(
              "Copied console content to terminal:",
              consoleOutputRef.current.length,
              "characters"
            );
          }
          return updated;
        });
      }

      // Initialize the Kubernetes terminal connection for this specific terminal
      const createWebSocketConnection = async () => {
        const getTerminalToken = async () => {
          const freshSupabaseJWT = await getUnexpiredToken(session);
          const terminalToken = (
            await axios.get(
              `${
                import.meta.env.VITE_BACKEND_BASE_URL
              }/canvases/${canvasId}/websocketToken`,
              {
                headers: {
                  Authorization: `Bearer ${freshSupabaseJWT}`,
                },
              }
            )
          )?.data?.token;
          return terminalToken;
        };

        const terminalToken = await getTerminalToken();

        // Create WebSocket with the specific terminal ID
        let socket = new WebSocket(
          `${
            import.meta.env.VITE_BACKEND_WS_BASE_URL
          }/canvases/${canvasId}/terminal?token=${terminalToken}&terminalId=${currentTerminalId}`
        );

        let reconnectAttempts = 0;
        const maxReconnectAttempts = 5;
        let reconnectTimeout: NodeJS.Timeout;

        const setupSocketEventListeners = (ws: WebSocket) => {
          ws.addEventListener("open", () => {
            console.log(
              `Terminal WebSocket connected for terminal ${currentTerminalId}`
            );
            reconnectAttempts = 0;
          });

          ws.addEventListener("close", (event) => {
            console.log(
              `Terminal WebSocket closed for terminal ${currentTerminalId}:`,
              event.code,
              event.reason
            );
            if (event.code !== 1000) {
              handleReconnect();
            }
          });

          ws.addEventListener("error", (event) => {
            console.error(
              `Terminal WebSocket error for terminal ${currentTerminalId}:`,
              event
            );
            handleReconnect();
          });

          ws.addEventListener("message", (event) => {
            terminalInstance.write(event.data);
          });
        };

        const handleReconnect = () => {
          if (reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            console.log(
              `Terminal reconnect attempt ${reconnectAttempts} for terminal ${currentTerminalId}`
            );

            reconnectTimeout = setTimeout(async () => {
              try {
                if (socket && socket.readyState !== WebSocket.CLOSED) {
                  socket.close(1000, "Reconnecting");
                }

                const newTerminalToken = await getTerminalToken();

                socket = new WebSocket(
                  `${
                    import.meta.env.VITE_BACKEND_WS_BASE_URL
                  }/canvases/${canvasId}/terminal?token=${newTerminalToken}&terminalId=${currentTerminalId}`
                );

                setupSocketEventListeners(socket);
              } catch (error) {
                console.error(
                  `Failed to reconnect terminal ${currentTerminalId}:`,
                  error
                );
                if (reconnectAttempts < maxReconnectAttempts) {
                  handleReconnect();
                }
              }
            }, 1000 * reconnectAttempts);
          }
        };

        setupSocketEventListeners(socket);

        terminalInstance.onData((data: string) => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(data);
            if (data === "\r") {
              terminalInstance.write("\r\n");
            }
          }
        });

        terminalInstance.onKey(({ key, domEvent }: any) => {
          if (domEvent.ctrlKey && domEvent.key === "c") {
            if (socket.readyState === WebSocket.OPEN) {
              socket.send("\x03");
            }
          }
        });

        return () => {
          if (reconnectTimeout) {
            clearTimeout(reconnectTimeout);
          }
          if (socket && socket.readyState !== WebSocket.CLOSED) {
            socket.close(1000, "Component cleanup");
          }
        };
      };

      return createWebSocketConnection();
    }

    let terminal, resizeObserver, wsCleanupFunction;

    // Cleanup function
    const cleanupFunction = () => {
      if (wsCleanupFunction) {
        wsCleanupFunction();
      }

      if (terminal) {
        terminal.dispose();
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };

    try {
      const { Terminal } = await import("@xterm/xterm");
      const { FitAddon } = await import("@xterm/addon-fit");

      if (!terminalContainerRef.current) {
        return;
      }

      if (!parentContainer.current) {
        return;
      }

      if (previousTerminalRef.current) {
        try {
          previousTerminalRef.current.dispose();
          previousTerminalRef.current = null;
        } catch (e) {}
      }

      terminal = new Terminal({
        theme: {
          background: "#1a1b1d",
          foreground: "white",
          cursor: "#6448ef",
          // set other color roles as needed
        },
        fontSize: 12,
        fontWeight: 500,
        cursorInactiveStyle: "outline",
        cols: 100, // Set explicit column count to prevent 80-character limit
      });
      previousTerminalRef.current = terminal;

      const fitAddon = new FitAddon();
      terminalAddonRef.current = fitAddon;

      terminal.loadAddon(fitAddon);
      terminal.open(terminalContainerRef.current);

      const supabaseJWT = await getUnexpiredToken(session);

      if (project?.runMode === ProjectRunMode.CLOUD) {
        const terminalToken = (
          await axios.get(
            `${
              import.meta.env.VITE_BACKEND_BASE_URL
            }/canvases/${canvasId}/websocketToken`,
            {
              headers: {
                Authorization: `Bearer ${supabaseJWT}`,
              },
            }
          )
        )?.data?.token;
        const socket = new WebSocket(
          `${
            import.meta.env.VITE_BACKEND_WS_BASE_URL
          }/canvases/${canvasId}/terminal?token=${terminalToken}`
        );

        socket.addEventListener("message", (event) => {
          terminal.write(event.data);
        });

        terminal.onData((data) => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(data);
            // Prevent terminal from closing on certain keystrokes
            if (data === "\r") {
              // Enter key
              terminal.write("\r\n");
            }
          }
        });

        terminal.onKey(({ key, domEvent }) => {
          // Handle Ctrl+C to prevent terminal from closing
          if (domEvent.ctrlKey && domEvent.key === "c") {
            socket.send("\x03"); // Send SIGINT
          }
        });

        wsCleanupFunction = () => {
          if (socket) {
            socket.close();
          }
        };
      }

      const resizeTerminal = () => {
        setTimeout(() => {
          if (terminalAddonRef.current) {
            terminalAddonRef.current.fit();
          }
        }, 10);
      };

      // Create a ResizeObserver
      resizeObserver = new ResizeObserver((entries) => {
        if (entries?.length) {
          resizeTerminal();
        }
      });

      if (parentContainer.current) {
        // Start observing the terminal container
        resizeObserver.observe(parentContainer.current);
      }

      // Initial resize
      resizeTerminal();
    } catch (e) {
      logAxiosError("Failed to initialize terminal", e);
    }

    return cleanupFunction;
  };

  /**
   * Connect terminal
   */
  useEffect(() => {
    if (!connect) {
      return;
    }

    if (!canvasId || !session || !project) {
      return;
    }

    if (isLocalMode) {
      return;
    }

    if (
      project?.runMode === ProjectRunMode.CLOUD &&
      !isKubernetesMode() &&
      !containerId
    ) {
      return;
    }

    // Kubernetes mode: Use enhanced state management
    if (isKubernetesMode()) {
      const currentTerminalState = getTerminalState(selectedTab);
      if (
        selectedTab !== Tabs.SERVER &&
        !currentTerminalState.initialized &&
        !currentTerminalState.initializing
      ) {
        // Set this terminal as initializing
        setTerminalState(selectedTab, { initializing: true });

        // Initialize the terminal
        initTerminal()
          .then(() => {
            setTerminalState(selectedTab, {
              initialized: true,
              initializing: false,
            });
          })
          .catch(() => {
            setTerminalState(selectedTab, { initializing: false });
          });
      }
      return;
    }

    // Docker mode: Original logic (unchanged)
    let mounted = true;
    let cleanupFunction: (() => void) | undefined;
    initTerminal().then((cleanup?: any) => {
      if (mounted) {
        cleanupFunction = cleanup;
      } else if (cleanup) {
        try {
          cleanup();
        } catch (e) {}
      }
    });

    return () => {
      mounted = false;
      if (cleanupFunction) {
        try {
          cleanupFunction();
        } catch (e) {}
      }
    };
  }, [
    canvasId,
    Boolean(session),
    projectLoadingProgress?.launched_container,
    parentContainerMounted,
    terminalContainerMounted,
    reconnectTrigger,
    isLocalMode,
    connect,
    // Add selectedTab back and terminalStates to handle per-terminal initialization
    selectedTab,
    terminalStates,
    // Remove the shared kubernetesTerminal state since we're using per-terminal state now
    // kubernetesTerminal.terminalInitialized,
    // kubernetesTerminal.terminalInitializing,
  ]);

  const initDevServer = async (
    consoleEndpoint: string,
    containerRef: React.MutableRefObject<any>,
    fitAddonRef: React.MutableRefObject<any>,
    tryIndex: number
  ) => {
    if (!containerRef.current || !project) {
      return;
    }

    if (previousDeverServerTerminalRef.current) {
      try {
        previousDeverServerTerminalRef.current.dispose();
        previousDeverServerTerminalRef.current = null;
      } catch (e) {}
    }

    setDevServerSocketStatus(SocketStatus.Connecting);
    // @ts-expect-error TS2345
    setLastConnectingTime(new Date());
    setReconnecting(false);

    let terminal, resizeObserver, wsCleanupFunction;

    // Cleanup function
    const cleanupFunction = () => {
      if (tryIndex === highestDevServerTryIndex.current) {
        setDevServerSocketStatus(SocketStatus.Idle);
      }

      if (terminal) {
        terminal.dispose();
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      if (wsCleanupFunction) {
        wsCleanupFunction();
      }
    };

    try {
      const { Terminal } = await import("@xterm/xterm");
      const { FitAddon } = await import("@xterm/addon-fit");

      terminal = new Terminal({
        theme: {
          background: "#1a1b1d",
          foreground: "white",
          cursor: "#6448ef",
          // set other color roles as needed
        },
        fontSize: 12,
        fontWeight: 500,
        cursorInactiveStyle: "outline",
      });
      previousDeverServerTerminalRef.current = terminal;

      const fitAddon = new FitAddon();
      fitAddonRef.current = fitAddon;

      terminal.loadAddon(fitAddon);
      terminal.open(containerRef.current);

      if (project?.runMode === ProjectRunMode.CLOUD) {
        const token = await getUnexpiredToken(session);

        const websocketToken = (
          await axios.get(
            `${
              import.meta.env.VITE_BACKEND_BASE_URL
            }/canvases/${canvasId}/websocketToken`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
        )?.data?.token;

        const socket = new WebSocket(
          `${
            import.meta.env.VITE_BACKEND_WS_BASE_URL
          }/canvases/${canvasId}/${consoleEndpoint}?token=${websocketToken}`
        );

        // Store the socket for control commands
        devServerSocketRef.current = socket;

        // Add Ctrl+C handler for the Console tab to stop the dev server
        terminal.onKey(({ key, domEvent }) => {
          console.log(
            "Key pressed:",
            key,
            "Ctrl:",
            domEvent.ctrlKey,
            "Key:",
            domEvent.key
          );

          if (domEvent.ctrlKey && domEvent.key === "c") {
            console.log(
              "Ctrl+C detected! Server running:",
              devServerStatus?.isServerRunning
            );

            domEvent.preventDefault();
            domEvent.stopPropagation();

            // Both behaviors enabled: send pause signal AND do visual switch

            // Option 1: Send stop command through the dev server WebSocket
            if (socket && socket.readyState === WebSocket.OPEN) {
              socket.send(
                JSON.stringify({
                  type: "control",
                  command: "stop_dev_server",
                })
              );
              console.log("Ctrl+C sent stop command to dev server");
            } else {
              console.log(
                "Socket not available or not open. Socket state:",
                socket?.readyState
              );
            }

            // Option 2: Create new terminal and hide console
            // Use consistent ID and name for console replacement
            const terminalId = "console-terminal";

            // Check if terminal already exists
            const existingTerminal = terminals.find((t) => t.id === terminalId);
            if (!existingTerminal) {
              const newTerminal = {
                id: terminalId,
                name: "Terminal",
              };

              // Add the new terminal
              setTerminals((prev) => [...prev, newTerminal]);
            }

            // Switch to the new terminal
            setSelectedTab(terminalId);
          }
        });

        // Focus the terminal when it's ready to receive input
        terminal.focus();

        socket.addEventListener("error", (event) => {
          if (tryIndex === highestDevServerTryIndex.current) {
            setDevServerSocketStatus(SocketStatus.Error);
          }
        });

        socket.addEventListener("close", (event) => {
          if (tryIndex === highestDevServerTryIndex.current) {
            setDevServerSocketStatus(SocketStatus.Closed);
          }
        });

        socket.addEventListener("open", () => {
          if (tryIndex === highestDevServerTryIndex.current) {
            setDevServerSocketStatus(SocketStatus.Open);
          }
        });

        // The first connection is a long history of previous logs,
        // so we might want to ignore those errors
        let isFirstConnection = true;
        let errorsData = "";
        let convexData = "";
        let expoData = "";
        let allEvents = [];
        socket.addEventListener("message", (event) => {
          // 1 seconds after first message, these are new logs
          setTimeout(() => {
            isFirstConnection = false;
          }, 1000);

          // Bug: Oct 22, 2024: the vite dev server doesn't pick up the changes in the codebase
          // Restarting the server fixes this issue
          if (
            event?.data?.includes?.(
              "Pre-transform error: Failed to load url"
            ) &&
            event?.data?.includes?.("tempobook/")
          ) {
            // Restart server
            if (
              projectLaunchStatusRef.current === "succeeded" &&
              !restartedServerFromErrorRef.current
            ) {
              restartedServerFromErrorRef.current = true;
              startServerRef.current({
                forceRestart: true,
              });
            }
          } else if (
            event?.data?.includes?.(
              "Error: listen EADDRINUSE: address already in use"
            )
          ) {
            // Relaunch container server
            if (!restartedServerFromErrorRef.current) {
              restartedServerFromErrorRef.current = true;
              launchContainerRef
                .current({
                  forceRelaunch: true,
                })
                .then(({ success, error }) => {
                  if (success) {
                    startServerRef.current();
                  }
                });
            }
          }

          // @ts-expect-error TS2345
          allEvents.push(event.data);

          // Detect convex login url
          convexData += event.data;
          const url = convexData.match(
            /Visit https:\/\/auth\.convex\.dev\/activate\?user_code=([A-Z0-9\-]+)/
          );

          const convexUrl = url?.[0]?.split(" ")?.[1];

          if (convexUrl) {
            if (!userLoggedIn && !isFirstConnection) {
              window.open(convexUrl, "_blank");
            }

            if (!isFirstConnection) {
              setConvexLoginUrl(convexUrl);
            }
            convexData = "";
          }

          // Detect Expo QR code URL
          expoData += event.data;
          const expoMatch = expoData.match(
            /Metro waiting on (exp:\/\/[\w\.-]+-\d+\.exp\.direct)/
          );

          if (expoMatch && expoMatch[1]) {
            const expoUrl = expoMatch[1];
            setExpoQrCodeUrl(expoUrl);
            expoData = "";
          }

          // Detect terminal errors
          errorsData += event.data;
          const viteErrors = detectViteErrors(errorsData);

          if (viteErrors?.errors?.length) {
            // Special case: if it's the first connection, wait 5 seconds and see if
            // any storyboards load, if not, then we can assume the errors from the history
            // still apply (after the project finished loading)
            if (isFirstConnection) {
              // Add cleanup of previous interval if exists
              let intervalId;
              const timeoutId = setTimeout(() => {
                clearInterval(intervalId);
              }, 30000);

              intervalId = setInterval(() => {
                if (projectLaunchStatusRef.current === "succeeded") {
                  clearInterval(intervalId);
                  clearTimeout(timeoutId);
                  if (anyErrorsAtAllRef.current) {
                    addTerminalErrors(viteErrors.errors);
                  }
                }
              }, 1000);
            } else {
              // Add the errors to the global state and clear allData
              addTerminalErrors(viteErrors.errors);
            }
            errorsData = ""; // Clear accumulated data after processing
          }

          terminal.write(event.data);
          fitAddon.fit();
          terminal.scrollToBottom();

          // Track console output for potential copying to new terminals
          consoleOutputRef.current += event.data;
          setConsoleOutput(consoleOutputRef.current);
        });

        terminal.onData((data) => {
          if (readOnly) {
            return;
          }

          socket.send(data);
        });

        wsCleanupFunction = () => {
          socket.close();
          devServerSocketRef.current = null;
        };
      }

      const resizeServer = () => {
        if (fitAddonRef.current) {
          fitAddonRef.current.fit();
        }
      };

      // Create a ResizeObserver
      resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          resizeServer();
        }
      });

      if (parentContainer.current) {
        // Start observing the terminal container
        resizeObserver.observe(parentContainer.current);
      }

      setTimeout(() => {
        fitAddon.fit();
      }, 0);
    } catch (e) {
      logAxiosError("Failed to initialize dev server terminal", e);
    }

    return cleanupFunction;
  };

  const prevTriggerReconnect = usePrevious(reconnectTrigger);

  /**
   * Trigger the reconnect separately from the other use effect so that we
   * are more deliverate about when to connect/reconnect
   */
  useEffect(() => {
    if (
      devServerSocketStatus === SocketStatus.Closed ||
      devServerSocketStatus === SocketStatus.Error ||
      devServerSocketStatus === SocketStatus.Idle
    ) {
      setReconnectTrigger(reconnectTrigger + 1);
    }
  }, [
    devServerSocketStatus,
    projectLaunchStatus,
    projectLoadingProgress?.launched_container,
  ]);

  /**
   * Initialize console streams.
   */
  useEffect(() => {
    if (!connect) {
      return;
    }

    if (!parentContainerMounted || !serverContainerMounted) {
      return;
    }

    if (
      !(
        devServerSocketStatus === SocketStatus.Idle ||
        devServerSocketStatus === SocketStatus.Closed
      ) &&
      reconnectTrigger === prevTriggerReconnect
    ) {
      return;
    }

    if (!project?.runMode) {
      return;
    }

    if (isLocalMode) {
      return;
    }

    if (!session) {
      return;
    }

    const tryIndex = highestDevServerTryIndex.current + 1;
    highestDevServerTryIndex.current = tryIndex;

    let mounted = true;
    let cleanupFunction: (() => void) | undefined;
    initDevServer(
      "devServer",
      serverContainerRef,
      serverAddonRef,
      tryIndex
    ).then((cleanup?: any) => {
      // Only set the cleanup if the component is still mounted
      if (mounted) {
        cleanupFunction = cleanup;
      } else if (cleanup) {
        // If we already unmounted, call cleanup immediately
        try {
          cleanup();
        } catch (e) {}
      }
    });

    return () => {
      mounted = false;
      if (cleanupFunction) {
        try {
          cleanupFunction();
        } catch (e) {}
      }
    };
  }, [
    containerId,
    project?.runMode,
    Boolean(session),
    parentContainerMounted,
    serverContainerMounted,
    projectLoadingProgress?.launched_container,
    reconnectTrigger,
    connect,
  ]);

  useEffect(() => {
    if (projectLaunchStatus === "succeeded") {
      // Clear this state so it can be restarted again
      restartedServerFromErrorRef.current = false;
    }
  }, [projectLaunchStatus]);

  const consoleSettings = useMemo(() => {
    return (
      <>
        <Button
          onClick={() => {
            // Clear the console based on the selected tab
            if (isKubernetesMode()) {
              // For Kubernetes mode, clear the appropriate terminal instance
              if (selectedTab === Tabs.SERVER) {
                // Clear the dev server terminal (Console tab)
                if (previousDeverServerTerminalRef.current) {
                  previousDeverServerTerminalRef.current.clear();
                }
              } else {
                // Clear the specific terminal instance
                const terminalInstance = terminalInstances[selectedTab];
                if (terminalInstance) {
                  terminalInstance.clear();
                }
              }
            } else {
              // For Docker mode, use the original logic
              if (
                selectedTab === Tabs.TERMINAL &&
                previousTerminalRef.current
              ) {
                previousTerminalRef.current.clear();
              } else if (
                selectedTab === Tabs.SERVER &&
                previousDeverServerTerminalRef.current
              ) {
                previousDeverServerTerminalRef.current.clear();
              }
            }
          }}
          variant="ghost"
          size="xs"
          className="my-1"
        >
          Clear
        </Button>
        <Button
          variant="ghost"
          className="my-1"
          size="xs"
          onClick={() => {
            if (devServerStatus?.isServerRunning) {
              // Server is running - restart it
              startServer({
                forceRestart: true,
              });
            } else {
              // Server is not running - send start command via WebSocket
              if (
                devServerSocketRef.current &&
                devServerSocketRef.current.readyState === WebSocket.OPEN
              ) {
                devServerSocketRef.current.send(
                  JSON.stringify({
                    type: "control",
                    command: "start_dev_server",
                  })
                );
                console.log(
                  "Start button clicked - sent start command to dev server"
                );
              } else {
                // Fallback to regular startServer if WebSocket not available
                startServer({
                  forceRestart: false,
                });
              }
            }

            // Reverse process: Switch back to console when starting the server
            // Find any terminal tabs (not the SERVER tab)
            const terminalTabs = terminals.filter((t) => t.id !== Tabs.SERVER);
            if (terminalTabs.length > 0) {
              // Switch back to console
              setSelectedTab(Tabs.SERVER);

              // Close the console terminal that was created from Ctrl+C
              const consoleTerminal = terminals.find(
                (t) => t.id === "console-terminal"
              );
              if (consoleTerminal) {
                closeTerminal("console-terminal");
              }
            }
          }}
        >
          {devServerStatus?.isServerRunning ? "Restart" : "Start"}
        </Button>
        <Button
          variant="ghost"
          className="my-1 mr-2"
          size="xs"
          onClick={async () => {
            const { success, error } = await launchContainer({
              forceRelaunch: true,
            });
            if (success) {
              startServer({
                forceRestart: true,
              });
            }
          }}
        >
          Relaunch
        </Button>
      </>
    );
  }, [devServerStatus]);

  const closeTerminal = (terminalId: string) => {
    // Don't close if it's the last terminal
    if (terminals.length <= 1) return;

    // Remove terminal from array
    setTerminals(terminals.filter((t) => t.id !== terminalId));

    // If we're closing the currently selected terminal, switch to another one
    if (selectedTab === terminalId) {
      const remainingTerminals = terminals.filter((t) => t.id !== terminalId);
      setSelectedTab(remainingTerminals[0]?.id || Tabs.SERVER);
    }
  };

  const onReconnect = () => {
    setReconnectTrigger(reconnectTrigger + 1);
    setReconnecting(true);
  };

  const showReconnect =
    (devServerSocketStatus === SocketStatus.Error ||
      devServerSocketStatus === SocketStatus.Closed ||
      // Has been connecting for over 10 seconds
      (devServerSocketStatus === SocketStatus.Connecting &&
        // @ts-expect-error TS18047
        new Date().getTime() - lastConnectingtime.getTime() > 10000)) &&
    projectLoadingProgress?.launched_container;

  useEffect(() => {
    if (convexLoginUrl && !userLoggedIn) {
      setShowConvexLoginDialog(true);
    } else {
      setShowConvexLoginDialog(false);
    }
  }, [convexLoginUrl, userLoggedIn]);

  const handleOpenConvexLogin = () => {
    setShowConvexLoginDialog(false);
    // @ts-expect-error TS2345
    setConvexLoginUrl(null);
    if (convexLoginUrl) {
      window.open(convexLoginUrl, "_blank");
    }
  };

  const handleParentRef = useCallback((ref: HTMLDivElement | null) => {
    parentContainer.current = ref || undefined;
    setParentContainerMounted(!!ref);
  }, []);

  const handleTerminalRef = useCallback((ref: HTMLDivElement | null) => {
    terminalContainerRef.current = ref || undefined;
    setTerminalContainerMounted(!!ref);
  }, []);

  return (
    <div
      className="w-full h-full border text-xs text-white flex flex-col gap-y-1 font-medium pl-1 bg-secondary-100"
      ref={handleParentRef}
    >
      <div
        className={cn(
          "flex flex-row justify-between w-full items-center",
          readOnly && "hidden"
        )}
      >
        <div className="flex flex-row space-x-2 pl-2 items-end">
          {terminals.map((terminal) => (
            <div key={terminal.id} className="flex items-center space-x-1">
              <p
                className={cn(
                  "cursor-pointer",
                  selectedTab === terminal.id
                    ? "border-b border-b-white"
                    : "border-b border-transparent"
                )}
                onClick={() => setSelectedTab(terminal.id)}
              >
                {terminal.name}
              </p>
              {terminals.length > 1 && terminal.id !== Tabs.SERVER && (
                <button
                  className="text-gray-400 hover:text-white text-xs px-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeTerminal(terminal.id);
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <p
            className="cursor-pointer flex justify-center items-center rounded-sm hover:bg-secondary-500"
            onClick={() => {
              // Generate unique terminal ID and name
              const terminalId = `terminal-${Date.now()}-${Math.random()
                .toString(36)
                .substring(2, 8)}`;
              const newTerminal = {
                id: terminalId,
                name: "Terminal", // Simple name like bash tabs
              };
              setTerminals([...terminals, newTerminal]);
              setSelectedTab(newTerminal.id);
            }}
          >
            <PlusIcon className="w-4 h-4 p-0.5" />
          </p>
        </div>
        <div className="flex flex-row">{consoleSettings}</div>
      </div>
      <div
        className={cn(
          "px-2 flex-grow overflow-hidden",
          selectedTab === Tabs.SERVER && "hidden"
        )}
      >
        <div className="h-full overflow-hidden" ref={handleTerminalRef} />
      </div>
      <div
        className={cn(
          "px-2 flex-grow overflow-hidden flex flex-col",
          (selectedTab !== Tabs.SERVER ||
            devServerSocketStatus !== SocketStatus.Open) &&
            "hidden"
        )}
      >
        <div
          className="flex-grow overflow-hidden cursor-text"
          ref={(ref) => {
            // @ts-expect-error TS2322
            serverContainerRef.current = ref;
            setServerContainerMounted(true);
          }}
          onClick={() => {
            // Focus the terminal when clicking on the Console area
            if (previousDeverServerTerminalRef.current) {
              previousDeverServerTerminalRef.current.focus();
            }
          }}
        />
      </div>
      {selectedTab === Tabs.SERVER &&
        devServerSocketStatus !== SocketStatus.Open && (
          <div className="p-2 w-full h-full flex flex-col gap-1">
            <ConsoleLoader
              count={25}
              // @ts-expect-error TS2322
              onReconnect={showReconnect ? onReconnect : null}
              reconnecting={reconnecting}
            />
          </div>
        )}

      <AlertDialog
        open={showConvexLoginDialog}
        onOpenChange={setShowConvexLoginDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Convex Login</AlertDialogTitle>
            <AlertDialogDescription>
              Click this link to login to Convex. If this link doesn't work,
              click on the Convex tab and click to connect there.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-2">
            <AlertDialogAction
              onClick={() => {
                setShowConvexLoginDialog(false);
                // @ts-expect-error TS2345
                setConvexLoginUrl(null);
              }}
              variant="secondary"
            >
              Dismiss
            </AlertDialogAction>
            <AlertDialogAction
              onClick={handleOpenConvexLogin}
              variant="secondary"
            >
              Open Convex Login
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default React.memo(Console);
